import { create as createTerminalTransport } from "../transports/winston-terminal-transport";
import { create as createFileTransport } from "../transports/winston-daily-rotate-file";
import levels from "../config";
import winston from "winston";

// The maximum number of logs which we keep before sending to CentralLogger.
// On average, FEA generates ~300 messages during startup before the central logger service becomes available
const MAX_SAVED_LOGS_COUNT: number = 4000;

export {};
export class LoggerMain {
	initialized: boolean;

	winston: winston.Logger;

	fileTransport: any;

	terminalTransport: any;

	// Queues log messages until Finsemble's logger service (central logger) is ready to receive them
	logsQueue: any[] | null;

	sendLogToCentralLogger: ((message: any) => void) | null = null;

	// Needed because sendLogToCentralLogger will be null until the function is set by enableLoggingToCentralLogger and
	// enableLoggingToCentralLogger is called regardless of whether logging to the central logger is enabled. So, it is
	// possible for sendToCentralLogger to be defined but for logToCentralLogger is disabled.
	logToCentralLoggerEnabled: boolean = true;

	centralLoggerLogLevels: any = {};

	errorCallback: ((...args: any[]) => void) | null = null;

	/**
	 * Create LoggerMain instance
	 * @param {EventEmitter} ipcMain Electron's ipcMain
	 */
	constructor(winstonLogger: winston.Logger) {
		this.initialized = false;
		this.winston = winstonLogger;
		this.logsQueue = [];
		// Bind correct context
		this.onLog = this.onLog.bind(this);
	}

	/**
	 * This should be called immediately in the main app. It creates the file transport.
	 */
	initialize(appDataPath: string, loggingMaxFiles: number | string = 3, loggingMaxSize: string = "5m", sanitizingRegExes: string[] = []) {
		this.initialized = true;

		let regExes: RegExp[] = [];
		(sanitizingRegExes ?? []).forEach((regex) => {
			if (typeof regex === "string") {
				try {
					regExes.push(new RegExp(regex, "g"));
				} catch (error) {
					console.error(`Error parsing regex "${regex}" in sanitizingRegExes. This regex will be ignored.`, error);
				}
			}
		});

		this.fileTransport = createFileTransport(appDataPath, loggingMaxFiles, loggingMaxSize, regExes);
		this.winston.add(this.fileTransport);
	}

	/**
	 * Enables logging to the CentralLogger
	 *
	 * @param logToCentralLogger the function to send messages via mainBus
	 */
	enableLoggingToCentralLogger(logToCentralLogger: (message: any) => void) {
		this.sendLogToCentralLogger = logToCentralLogger;

		// release queue
		this.logsQueue?.forEach((msg) => this.sendLogToCentralLoggerBasedOnLevel(msg));
		this.logsQueue = null;
	}

	/**
	 *
	 * @param msg
	 */
	sendLogToCentralLoggerBasedOnLevel = (msg: any) => {
		if (this.sendLogToCentralLogger === null) {
			if (this.logsQueue !== null) {
				// save messages in a circular queue to prevent overflow memory
				this.logsQueue[this.logsQueue.length % MAX_SAVED_LOGS_COUNT] = msg;
			}
		} else if (this.centralLoggerLogLevels[msg.level]) {
			this.sendLogToCentralLogger?.(msg);
		}
	};

	/**
	 * Updates enabled log levels from the CentralLogger
	 */
	updateCentralLoggerState(loggerState: any) {
		if (loggerState?.system) {
			this.centralLoggerLogLevels = loggerState.system;
		}
	}

	/**
	 * Enables the console transport
	 */
	enableConsole() {
		this.initialized = true;
		this.terminalTransport = createTerminalTransport();
		this.winston.add(this.terminalTransport);
	}

	/**
	 * Sets the minimum log level on all transports
	 * @param {string} level The minimum logging level
	 */
	setLevel(level = "debug") {
		// The level "log" conflicts with Winston's log method.
		const logLevel = level.replace("log", "log-");
		if (!this.winston.isLevelEnabled(logLevel)) {
			const validOpts = Object.keys(levels).join('", "').replace("-", "");
			this.warn(`Bad logging level "${logLevel}" passed to setLevel. Valid options are: "${validOpts}".`);
		} else {
			this.debug(`Changed logging level to ${logLevel}`);
			if (this.terminalTransport) this.terminalTransport.level = logLevel;
			if (this.fileTransport) this.fileTransport.level = logLevel;
		}
	}

	/**
	 * Deals with log messages that are emitted from
	 * renderer process's logger through electron's ipc
	 * @param {event} event
	 * @param {object} message
	 * @return {void}
	 * @private
	 */
	onLog(event: any, message: any) {
		// Make sure that we have a valid message
		// if so, forward it to local logging methods
		if (message && message.level && message.args) {
			(this as any)[message.level](message.args);
		}
	}

	/**
	 * Configures logging to the CentralLogger depending on the manifest parameter
	 *
	 * @param transmitToCentralLogger the `manifest.finsemble-electron-adapter.logger.transmitToCentralLogger` manifest parameter
	 */
	setupLoggingToCentralLogger(transmitToCentralLogger?: boolean) {
		if (transmitToCentralLogger === false) {
			this.logToCentralLoggerEnabled = false;
			this.logsQueue = null;
		}
	}

	/**
	 * The function to transmit logs to the CentralLogger if that ability is enabled
	 *
	 * @param messages The log message(s)
	 * @param level The log level
	 */
	maybeSendToCentralLogger(messages: any[], level: string) {
		// If the message originated in the Central Logger (sent here by logToDisk) then don't send it back
		if (messages[0] === "Central Logger") return;
		if (this.logToCentralLoggerEnabled) {
			// Converts Error to string, because Error can't be JSON stringified
			const filteredArgs = messages.map((arg) => (arg instanceof Error ? arg.toString() : arg));

			const message = {
				time: Date.now(),
				logData: JSON.stringify(filteredArgs),
				level: level,
			};

			this.sendLogToCentralLoggerBasedOnLevel(message);
		}
	}

	setErrorCallback(cb: (...args: any[]) => void) {
		this.errorCallback = cb;
	}

	/**
	 * Logs a message of type error.
	 * @param {...*} messages The log message(s)
	 */
	error(...messages: any[]) {
		if (!this.initialized) console.warn("Log message received before winston Logger was initialized.", messages);
		this.winston.error(messages);
		this.errorCallback?.(messages);
		this.maybeSendToCentralLogger(messages, "Error");
	}

	/**
	 * Logs a message of type warn
	 * @param {...*} messages The log message(s)
	 */
	warn(...messages: any[]) {
		if (!this.initialized) console.warn("Log message received before winston Logger was initialized.", messages);
		this.winston.warn(messages);
		this.maybeSendToCentralLogger(messages, "Warn");
	}

	/**
	 * Logs a message of type info
	 * @param {...*} messages The log message(s)
	 */
	info(...messages: any[]) {
		if (!this.initialized) console.warn("Log message received before winston Logger was initialized.", messages);
		this.winston.info(messages);
		this.maybeSendToCentralLogger(messages, "Info");
	}

	/**
	 * Logs a message of type log
	 * @param {...*} messages The log message(s)
	 */
	log(...messages: any[]) {
		if (!this.initialized) console.warn("Log message received before winston Logger was initialized.", messages);
		(this.winston as any)["log-"](messages);
		this.maybeSendToCentralLogger(messages, "Log");
	}

	/**
	 * Logs a message of type debug
	 * @param {...*} messages The log message(s)
	 */
	debug(...messages: any[]) {
		if (!this.initialized) console.warn("Log message received before winston Logger was initialized.", messages);
		this.winston.debug(messages);
		this.maybeSendToCentralLogger(messages, "Debug");
	}

	/**
	 * Logs a message of type verbose
	 * @param {...*} messages The log message(s)
	 */
	verbose(...messages: any[]) {
		if (!this.initialized) console.warn("Log message received before winston Logger was initialized.", messages);
		this.winston.verbose(messages);
		this.maybeSendToCentralLogger(messages, "Verbose");
	}
}
